import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { usePortalAuth } from '../../hooks/usePortalAuth'
import { useDropinTicketsForUser } from '../../hooks/useDropinTicketsForUser'
import { usePortalQueryClientId } from '../../hooks/portalHostQueryGate'
import useAppState from '../../hooks/useAppState'
import { getGrooverText } from '../../libs/GrooverTextRepository'
import { DropinTicket, DropinTicketStatus } from '../../../../pure-js/types/GrooverTypes'
import { buildRoute } from '../../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../../pure-js/libs/RoutePath'
import { BuyDropinTicketsModal } from '../../components/portal/BuyDropinTicketsModal'
import { sortDropinTicketsByClips } from './portalDropinTicketHelpers'
import { PortalDropinTicketCard } from './PortalDropinTicketCard'
import { PortalDropinTicketDetailCard } from './PortalDropinTicketDetailCard'
import {
  BuyTicketsButton,
  DetailOverlay,
  EmptyDescription,
  EmptyIcon,
  EmptyState,
  EmptyTitle,
  ErrorContainer,
  ErrorIcon,
  ErrorText,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  MainContent,
  PageContainer,
  PageTitle,
  RetryButton,
  StickyBuyBar,
  Tab,
  TabsContainer,
  TicketsList
} from './PortalDropinTicketsPage.styles'

export const PortalDropinTicketsPage: React.FC = () => {
  const { portalUser, portalClaimsReady } = usePortalAuth()
  const { state } = useAppState()
  const navigate = useNavigate()
  const { dropinTicketId } = useParams<{ dropinTicketId?: string }>()
  const selectedLanguage = state.selectedLanguage
  const getText = useCallback((key: string) => getGrooverText(key, selectedLanguage), [selectedLanguage])
  const [selectedTicket, setSelectedTicket] = useState<DropinTicket | null>(null)
  const [showBuyModal, setShowBuyModal] = useState(false)
  const [activeTab, setActiveTab] = useState<'active' | 'other'>('active')
  const handleTabKeyDown = (e: React.KeyboardEvent, current: 'active' | 'other') => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault()
      setActiveTab(current === 'active' ? 'other' : 'active')
    }
  }

  const enrollmentUserId = portalClaimsReady ? (portalUser?.enrollmentUserId ?? null) : null
  const clientId = usePortalQueryClientId()
  const { data: tickets, isLoading, error } = useDropinTicketsForUser(enrollmentUserId, clientId)
  const visibleTickets = useMemo(
    () =>
      (tickets || []).filter(
        (t) => t.status !== DropinTicketStatus.PendingPayment && t.status !== DropinTicketStatus.Deleted
      ),
    [tickets]
  )
  const sortedAllTickets = useMemo(() => sortDropinTicketsByClips(visibleTickets), [visibleTickets])
  const activeTickets = useMemo(
    () => sortDropinTicketsByClips(visibleTickets.filter((t) => t.status === DropinTicketStatus.Active)),
    [visibleTickets]
  )
  const otherTickets = useMemo(
    () => sortDropinTicketsByClips(visibleTickets.filter((t) => t.status !== DropinTicketStatus.Active)),
    [visibleTickets]
  )

  // Open ticket from URL and auto-switch tab when the ticket is in the "other" group
  useEffect(() => {
    if (!dropinTicketId || !visibleTickets.length) return
    const ticket = visibleTickets.find((t) => t.id === dropinTicketId)
    if (!ticket) return
    setSelectedTicket(ticket)
    const isOther = ticket.status !== DropinTicketStatus.Active
    setActiveTab(isOther ? 'other' : 'active')
  }, [dropinTicketId, visibleTickets])

  const openTicket = useCallback(
    (ticket: DropinTicket) => {
      setSelectedTicket(ticket)
      navigate(buildRoute(RoutePath.PORTAL_DROPIN_TICKET_DETAIL, { dropinTicketId: ticket.id }))
    },
    [navigate]
  )

  const closeTicket = useCallback(() => {
    setSelectedTicket(null)
    navigate(buildRoute(RoutePath.PORTAL_DROPIN_TICKETS))
  }, [navigate])

  useEffect(() => {
    if (!selectedTicket) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeTicket()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [selectedTicket, closeTicket])

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>{getText('portalLoadingDropinTickets')}</LoadingText>
        </LoadingContainer>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorText>{getText('portalFailedToLoadDropinTickets')}</ErrorText>
          <RetryButton onClick={() => window.location.reload()}>{getText('portalTryAgain')}</RetryButton>
        </ErrorContainer>
      </PageContainer>
    )
  }

  if (visibleTickets.length === 0) {
    return (
      <PageContainer>
        <MainContent>
          <PageTitle>{getText('portalYourDropinTickets')}</PageTitle>
          <EmptyState>
            <EmptyIcon>🎫</EmptyIcon>
            <EmptyTitle>{getText('portalNoDropinTicketsTitle')}</EmptyTitle>
            <EmptyDescription>{getText('portalNoDropinTickets')}</EmptyDescription>
          </EmptyState>
          <StickyBuyBar>
            <BuyTicketsButton type="button" onClick={() => setShowBuyModal(true)}>
              <span>🎫</span>
              {getText('portalBuyTickets')}
            </BuyTicketsButton>
          </StickyBuyBar>
        </MainContent>
        <BuyDropinTicketsModal
          isOpen={showBuyModal}
          onClose={() => setShowBuyModal(false)}
          tickets={sortedAllTickets}
          getText={getText}
        />
      </PageContainer>
    )
  }

  const tabTickets = activeTab === 'active' ? activeTickets : otherTickets

  return (
    <PageContainer>
      <MainContent>
        <PageTitle>{getText('portalYourDropinTickets')}</PageTitle>
        <TabsContainer role="tablist">
          <Tab
            role="tab"
            type="button"
            $active={activeTab === 'active'}
            aria-selected={activeTab === 'active'}
            onClick={() => setActiveTab('active')}
            onKeyDown={(e) => handleTabKeyDown(e, 'active')}
          >
            {getText('portalDropinTicketsSectionActive')}
          </Tab>
          <Tab
            role="tab"
            type="button"
            $active={activeTab === 'other'}
            aria-selected={activeTab === 'other'}
            onClick={() => setActiveTab('other')}
            onKeyDown={(e) => handleTabKeyDown(e, 'other')}
          >
            {getText('portalDropinTicketsSectionUsedAndOther')}
          </Tab>
        </TabsContainer>
        {tabTickets.length === 0 ? (
          <EmptyState>
            <EmptyIcon>🎫</EmptyIcon>
            <EmptyTitle>
              {getText(activeTab === 'active' ? 'portalNoActiveDropinTickets' : 'portalNoOtherDropinTickets')}
            </EmptyTitle>
          </EmptyState>
        ) : (
          <TicketsList>
            {tabTickets.map((ticket) => (
              <PortalDropinTicketCard
                key={ticket.id}
                ticket={ticket}
                getText={getText}
                onClick={() => openTicket(ticket)}
              />
            ))}
          </TicketsList>
        )}
        <StickyBuyBar>
          <BuyTicketsButton type="button" onClick={() => setShowBuyModal(true)}>
            <span>🎫</span>
            {getText('portalBuyTickets')}
          </BuyTicketsButton>
        </StickyBuyBar>
      </MainContent>
      {selectedTicket && (
        <DetailOverlay
          role="dialog"
          aria-modal="true"
          aria-labelledby="dropin-ticket-detail-title"
          onClick={closeTicket}
        >
          <PortalDropinTicketDetailCard
            ticket={selectedTicket}
            enrollmentUserId={portalUser?.enrollmentUserId}
            getText={getText}
            selectedLanguage={selectedLanguage}
            onClose={closeTicket}
          />
        </DetailOverlay>
      )}
      <BuyDropinTicketsModal
        isOpen={showBuyModal}
        onClose={() => setShowBuyModal(false)}
        tickets={sortedAllTickets}
        getText={getText}
      />
    </PageContainer>
  )
}
